import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import config from '../../config';
import { encryptData } from '../../utils/cryptoUtils';
import '../common.scss';

// ----------------------------------------------------------------------
const emptyBillingRow = () => ({
  billing_period: '',
  no_of_vehicles: '',
  no_of_drivers: '',
  amount_excluding_gst: '',
  driver_cost: '',
  electric_cost: '',
  parking_expense: '',
  maintenance_cost: '',
  emi_per_month: '',
});

const emptyClientBlock = () => ({
  client_name: '',
  contract_start_date: '',
  contract_end_date: '',
  financial_terms: '',
  min_billing_per_month: '',
  billings: [emptyBillingRow()],
});
// ----------------------------------------------------------------------

const toNum = (v) => parseFloat(v) || 0;

const FleetViability = (props) => {
  const { leadId } = props;

  const [clientBlocks, setClientBlocks] = useState([emptyClientBlock()]);
  const [preloaderStatus, setPreloaderStatus] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  // ---- Fetch existing Fleet Viability data for this lead ----
  const getFleetViability = async () => {
    if (!leadId) return;
    try {
      setLoadingList(true);

      const obj = { lead_id: leadId };

      const response = await fetch(`${config.API_BASE_URL}getFleetViability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData(obj),
      });

      const result = await response.json();

      if (result?.success === 1) {
        const data = result?.data || [];
        if (Array.isArray(data) && data.length > 0) {
          setClientBlocks(data);
        } else {
          setClientBlocks([emptyClientBlock()]);
        }
      } else {
        setClientBlocks([emptyClientBlock()]);
      }
    } catch (error) {
      console.log('Fleet Viability fetch error:', error);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    getFleetViability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  // ---- Client header field change (client_name / contract fields) ----
  const handleClientHeaderChange = (cIndex, e) => {
    const { name, value } = e.target;
    setClientBlocks((prev) => {
      const updated = [...prev];
      updated[cIndex] = { ...updated[cIndex], [name]: value };
      return updated;
    });
  };

  // ---- Billing row field change ----
  const handleBillingChange = (cIndex, bIndex, e) => {
    const { name, value } = e.target;
    setClientBlocks((prev) => {
      const updated = [...prev];
      const billings = [...updated[cIndex].billings];
      billings[bIndex] = { ...billings[bIndex], [name]: value };
      updated[cIndex] = { ...updated[cIndex], billings };
      return updated;
    });
  };

  // ---- Add a new Client block (poora form copy hoke niche aajayega) ----
  const handleAddClient = () => {
    setClientBlocks((prev) => [...prev, emptyClientBlock()]);
  };

  // ---- Remove a Client block ----
  const handleRemoveClient = (cIndex) => {
    setClientBlocks((prev) => prev.filter((_, i) => i !== cIndex));
  };

  // ---- Add a new billing row inside a Client block ----
  const handleAddBilling = (cIndex) => {
    setClientBlocks((prev) => {
      const updated = [...prev];
      updated[cIndex] = {
        ...updated[cIndex],
        billings: [...updated[cIndex].billings, emptyBillingRow()],
      };
      return updated;
    });
  };

  // ---- Remove a billing row ----
  const handleRemoveBilling = (cIndex, bIndex) => {
    setClientBlocks((prev) => {
      const updated = [...prev];
      updated[cIndex] = {
        ...updated[cIndex],
        billings: updated[cIndex].billings.filter((_, i) => i !== bIndex),
      };
      return updated;
    });
  };

  // ---- Formula for a single billing row ----
  // Total Expense   = Driver Cost + Electric Cost + Parking Expense + Maintenance + EMI Per Month
  // Net Income      = Amount (Excl GST) - Total Expense
  // Income/Vehicle  = Net Income / No of Vehicles
  // FOIR            = (Total Expense / Amount Excl GST) * 100
  const getRowMetrics = (row) => {
    const amount = toNum(row.amount_excluding_gst);
    const driverCost = toNum(row.driver_cost);
    const electricCost = toNum(row.electric_cost);
    const parkingExpense = toNum(row.parking_expense);
    const maintenanceCost = toNum(row.maintenance_cost);
    const emiPerMonth = toNum(row.emi_per_month);
    const noOfVehicles = toNum(row.no_of_vehicles) || 1;

    const totalExpense = driverCost + electricCost + parkingExpense + maintenanceCost + emiPerMonth;
    const netIncome = amount - totalExpense;
    const incomePerVehicle = netIncome / noOfVehicles;
    const foir = amount > 0 ? (totalExpense / amount) * 100 : 0;

    return { totalExpense, netIncome, incomePerVehicle, foir };
  };

  // ---- Per-Client totals (like Excel Total row) ----
  const getClientTotals = (block) => {
    const billings = block.billings || [];

    let noOfVehicles = 0;
    let noOfDrivers = 0;
    let amount = 0;
    let driverCost = 0;
    let electricCost = 0;
    let parkingExpense = 0;
    let maintenanceCost = 0;
    let emiPerMonth = 0;
    let netIncome = 0;

    billings.forEach((row) => {
      const m = getRowMetrics(row);
      noOfVehicles += toNum(row.no_of_vehicles);
      noOfDrivers += toNum(row.no_of_drivers);
      amount += toNum(row.amount_excluding_gst);
      driverCost += toNum(row.driver_cost);
      electricCost += toNum(row.electric_cost);
      parkingExpense += toNum(row.parking_expense);
      maintenanceCost += toNum(row.maintenance_cost);
      emiPerMonth += toNum(row.emi_per_month);
      netIncome += m.netIncome;
    });

    const totalExpense = driverCost + electricCost + parkingExpense + maintenanceCost + emiPerMonth;
    const foir = amount > 0 ? (totalExpense / amount) * 100 : 0;
    const incomePerVehicle = noOfVehicles > 0 ? netIncome / noOfVehicles : 0;

    return {
      noOfVehicles,
      noOfDrivers,
      amount,
      driverCost,
      electricCost,
      parkingExpense,
      maintenanceCost,
      emiPerMonth,
      netIncome,
      incomePerVehicle,
      foir,
    };
  };

  // ---- Grand totals across all Clients ----
  const grandTotals = clientBlocks.reduce(
    (acc, block) => {
      const t = getClientTotals(block);
      acc.noOfVehicles += t.noOfVehicles;
      acc.noOfDrivers += t.noOfDrivers;
      acc.amount += t.amount;
      acc.driverCost += t.driverCost;
      acc.electricCost += t.electricCost;
      acc.parkingExpense += t.parkingExpense;
      acc.maintenanceCost += t.maintenanceCost;
      acc.emiPerMonth += t.emiPerMonth;
      acc.netIncome += t.netIncome;
      return acc;
    },
    {
      noOfVehicles: 0,
      noOfDrivers: 0,
      amount: 0,
      driverCost: 0,
      electricCost: 0,
      parkingExpense: 0,
      maintenanceCost: 0,
      emiPerMonth: 0,
      netIncome: 0,
    }
  );

  const grandTotalExpense =
    grandTotals.driverCost +
    grandTotals.electricCost +
    grandTotals.parkingExpense +
    grandTotals.maintenanceCost +
    grandTotals.emiPerMonth;

  const grandFoir = grandTotals.amount > 0 ? (grandTotalExpense / grandTotals.amount) * 100 : 0;
  const grandIncomePerVehicle = grandTotals.noOfVehicles > 0 ? grandTotals.netIncome / grandTotals.noOfVehicles : 0;

  const fmt = (n) => (n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 });

  const fmtPct = (n) => `${(n || 0).toFixed(2)}%`;

  // ---- Save everything ----
  const handleSave = async () => {
    const hasEmptyClient = clientBlocks.some((b) => (b.client_name || '').trim() === '');
    if (hasEmptyClient) {
      toast.error('Client Name is required for all blocks.', { position: 'top-right' });
      return;
    }

    try {
      setPreloaderStatus(true);

      const params = { lead_id: leadId, fleetViabilityData: clientBlocks };

      const response = await fetch(`${config.API_BASE_URL}manageFleetViability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData(params),
      });

      const result = await response.json();

      if (result?.success === 1) {
        toast.success(result.message, { position: 'top-right' });
        getFleetViability();
      } else {
        toast.error(result?.message || 'Something went wrong.', { position: 'top-right' });
      }
    } catch (error) {
      console.error('Error in Fleet Viability save:', error);
      toast.error('An error occurred. Please try again.', { position: 'top-right' });
    } finally {
      setPreloaderStatus(false);
    }
  };

  return (
    <div className="fleet_viability_section mt-3">
      {loadingList ? (
        <p>Loading Fleet Viability sheet...</p>
      ) : (
        <div>
          {clientBlocks.map((block, cIndex) => {
            const totals = getClientTotals(block);

            return (
              <div key={`client-${cIndex}`} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '24px' }}>
                {/* Index circle */}
                <div
                  style={{
                    width: '30px',
                    height: '30px',
                    minWidth: '30px',
                    borderRadius: '50%',
                    background: '#0d6efd',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '14px',
                    marginRight: '14px',
                    marginTop: '18px',
                    flexShrink: 0,
                  }}
                >
                  {cIndex + 1}
                </div>

                {/* Client block box */}
                <div className="border rounded p-3" style={{ position: 'relative', flex: 1, overflow: 'auto' }}>
                  {clientBlocks.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveClient(cIndex)}
                      title="Remove this Client"
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '26px',
                        height: '26px',
                        borderRadius: '50%',
                        border: 'none',
                        background: '#dc3545',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: '14px',
                        lineHeight: 1,
                        padding: 0,
                      }}
                    >
                      ×
                    </button>
                  )}

                  {/* Contract Wise Details */}
                  <h6 className="mb-3">Contract Wise Details</h6>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor={`client_name_${cIndex}`}>
                          <span>
                            Client Name <a style={{ color: 'red' }}>*</a>
                          </span>
                          <input
                            type="text"
                            id={`client_name_${cIndex}`}
                            name="client_name"
                            className="form-control"
                            placeholder="e.g. Delhivery Ltd"
                            value={block.client_name}
                            onChange={(e) => handleClientHeaderChange(cIndex, e)}
                          />
                        </label>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor={`contract_start_date_${cIndex}`}>
                          <span>Contract Start Date</span>
                          <input
                            type="date"
                            id={`contract_start_date_${cIndex}`}
                            name="contract_start_date"
                            className="form-control"
                            value={block.contract_start_date}
                            onChange={(e) => handleClientHeaderChange(cIndex, e)}
                          />
                        </label>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor={`contract_end_date_${cIndex}`}>
                          <span>Contract End Date</span>
                          <input
                            type="date"
                            id={`contract_end_date_${cIndex}`}
                            name="contract_end_date"
                            className="form-control"
                            value={block.contract_end_date}
                            onChange={(e) => handleClientHeaderChange(cIndex, e)}
                          />
                        </label>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor={`financial_terms_${cIndex}`}>
                          <span>Financial Terms</span>
                          <input
                            type="text"
                            id={`financial_terms_${cIndex}`}
                            name="financial_terms"
                            className="form-control"
                            placeholder="e.g. Rs 39000 Per Vehicle"
                            value={block.financial_terms}
                            onChange={(e) => handleClientHeaderChange(cIndex, e)}
                          />
                        </label>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor={`min_billing_per_month_${cIndex}`}>
                          <span>Minimum Billing Per Month as per Contract</span>
                          <input
                            type="number"
                            id={`min_billing_per_month_${cIndex}`}
                            name="min_billing_per_month"
                            className="form-control"
                            value={block.min_billing_per_month}
                            onChange={(e) => handleClientHeaderChange(cIndex, e)}
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Monthly Billing table */}
                  <h6 className="mt-4 mb-2">Monthly Billing Details</h6>
                  <div className="table-responsive mt-2">
                    <table className="table table-bordered table-sm align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{ minWidth: 120 }}>Billing Period</th>
                          <th style={{ minWidth: 100 }}>No of Vehicles</th>
                          <th style={{ minWidth: 100 }}>No of Drivers</th>
                          <th style={{ minWidth: 130 }}>Amount (Excl. GST)</th>
                          <th style={{ minWidth: 110 }}>Driver Cost</th>
                          <th style={{ minWidth: 150 }}>Electric Cost (10 Rs/Unit) + Diesel Expense</th>
                          <th style={{ minWidth: 110 }}>Parking Expense</th>
                          <th style={{ minWidth: 150 }}>Maintenance &amp; Repairing (Insurance + Spares)</th>
                          <th style={{ minWidth: 150 }}>EMI Per Month (CFPL, IDFC &amp; Lease Rental)</th>
                          <th style={{ minWidth: 120 }}>Net Income</th>
                          <th style={{ minWidth: 120 }}>Income Per Vehicle</th>
                          <th style={{ minWidth: 90 }}>FOIR</th>
                          <th style={{ width: 50 }}> </th>
                        </tr>
                      </thead>
                      <tbody>
                        {block.billings.map((row, bIndex) => {
                          const m = getRowMetrics(row);
                          return (
                            <tr key={`billing-${cIndex}-${bIndex}`}>
                              <td>
                                <input
                                  type="month"
                                  name="billing_period"
                                  className="form-control form-control-sm"
                                  value={row.billing_period}
                                  onChange={(e) => handleBillingChange(cIndex, bIndex, e)}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  name="no_of_vehicles"
                                  className="form-control form-control-sm"
                                  value={row.no_of_vehicles}
                                  onChange={(e) => handleBillingChange(cIndex, bIndex, e)}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  name="no_of_drivers"
                                  className="form-control form-control-sm"
                                  value={row.no_of_drivers}
                                  onChange={(e) => handleBillingChange(cIndex, bIndex, e)}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  name="amount_excluding_gst"
                                  className="form-control form-control-sm"
                                  value={row.amount_excluding_gst}
                                  onChange={(e) => handleBillingChange(cIndex, bIndex, e)}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  name="driver_cost"
                                  className="form-control form-control-sm"
                                  value={row.driver_cost}
                                  onChange={(e) => handleBillingChange(cIndex, bIndex, e)}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  name="electric_cost"
                                  className="form-control form-control-sm"
                                  value={row.electric_cost}
                                  onChange={(e) => handleBillingChange(cIndex, bIndex, e)}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  name="parking_expense"
                                  className="form-control form-control-sm"
                                  value={row.parking_expense}
                                  onChange={(e) => handleBillingChange(cIndex, bIndex, e)}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  name="maintenance_cost"
                                  className="form-control form-control-sm"
                                  value={row.maintenance_cost}
                                  onChange={(e) => handleBillingChange(cIndex, bIndex, e)}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  name="emi_per_month"
                                  className="form-control form-control-sm"
                                  value={row.emi_per_month}
                                  onChange={(e) => handleBillingChange(cIndex, bIndex, e)}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={fmt(m.netIncome)}
                                  readOnly
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={fmt(m.incomePerVehicle)}
                                  readOnly
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={fmtPct(m.foir)}
                                  readOnly
                                />
                              </td>
                              <td className="text-center">
                                {block.billings.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveBilling(cIndex, bIndex)}
                                    title="Remove this row"
                                    style={{
                                      width: '24px',
                                      height: '24px',
                                      borderRadius: '50%',
                                      border: 'none',
                                      background: '#dc3545',
                                      color: '#fff',
                                      cursor: 'pointer',
                                      fontSize: '13px',
                                      lineHeight: 1,
                                      padding: 0,
                                    }}
                                  >
                                    ×
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="table-light fw-bold">
                          <td>Total</td>
                          <td>{totals.noOfVehicles}</td>
                          <td>{totals.noOfDrivers}</td>
                          <td>{fmt(totals.amount)}</td>
                          <td>{fmt(totals.driverCost)}</td>
                          <td>{fmt(totals.electricCost)}</td>
                          <td>{fmt(totals.parkingExpense)}</td>
                          <td>{fmt(totals.maintenanceCost)}</td>
                          <td>{fmt(totals.emiPerMonth)}</td>
                          <td>{fmt(totals.netIncome)}</td>
                          <td>{fmt(totals.incomePerVehicle)}</td>
                          <td>{fmtPct(totals.foir)}</td>
                          <td> </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm mt-2"
                    onClick={() => handleAddBilling(cIndex)}
                  >
                    <i className="bi bi-plus-circle"> </i> Add Month
                  </button>
                </div>
              </div>
            );
          })}

          <div className="mb-4">
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAddClient}>
              <i className="bi bi-plus-circle"> </i> Add Client
            </button>
          </div>

          {/* Grand Total summary (jaise Excel "Total" row All Clients ke liye) */}
          <div className="border rounded p-3 mb-4" style={{ background: '#f8f9fa' }}>
            <h6 className="mb-3">All Clients - Summary</h6>
            <div className="row">
              <div className="col-md-3">
                <div className="text-muted">Total Vehicles</div>
                <div className="fw-bold">{grandTotals.noOfVehicles}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted">Total Drivers</div>
                <div className="fw-bold">{grandTotals.noOfDrivers}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted">Total Amount (Excl. GST)</div>
                <div className="fw-bold">{fmt(grandTotals.amount)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted">Total Expense</div>
                <div className="fw-bold">{fmt(grandTotalExpense)}</div>
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-md-4">
                <span className="text-muted">Net Income: </span>
                <strong>{fmt(grandTotals.netIncome)}</strong>
              </div>
              <div className="col-md-4">
                <span className="text-muted">Income Per Vehicle: </span>
                <strong>{fmt(grandIncomePerVehicle)}</strong>
              </div>
              <div className="col-md-4">
                <span className="text-muted">FOIR: </span>
                <strong className="text-primary">{fmtPct(grandFoir)}</strong>
              </div>
            </div>
          </div>

          <div className="row mx-0">
            <div className="col-md-12">
              <div className="form-group mb-0">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={preloaderStatus}>
                  {preloaderStatus ? 'Saving...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetViability;
